// @ts-strict-ignore
import { FilterAction, FilterState } from '../reducer'
import { Accordion, Background, Content, Footer, Header } from '@components/FullscreenFilterModal'
import { Checkbox } from '@gassan-ui'
import { Item } from '@lib/kontent/fetchContent'
import { BrandPage } from '@lib/kontent/models/BrandPage'
import { StoreService } from '@lib/kontent/models/StoreService'
import { useTranslation } from 'next-i18next'
import React, { useState } from 'react'

type Props = {
  onRequestClose?: () => void
  dispatch: React.Dispatch<FilterAction>
  brands?: Item<BrandPage>[]
  services?: Item<StoreService>[]
  filters: FilterState
}

const FilterOverlay = React.forwardRef<HTMLDivElement, Props>(
  ({ onRequestClose, dispatch, filters, services, brands }, ref) => {
    const { t } = useTranslation(['stores', 'other'])
    const [state, setState] = useState<FilterState>(filters)

    const toggle = (key: string, value: string) => {
      if (state[key]) {
        setState((state) => ({
          ...state,
          [key]: state[key].includes(value)
            ? state[key].filter((i) => i !== value)
            : [...state[key], value],
        }))
      } else {
        setState((state) => ({
          ...state,
          [key]: [value],
        }))
      }
    }

    const total = Object.keys(state).reduce((prev, key) => prev + state[key]?.length || 0, 0)

    const handleSave = () => {
      dispatch({ type: 'set-filters', filters: state })
      onRequestClose()
    }

    return (
      <>
        <Background
          key="filters-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Header
            onClickClose={onRequestClose}
            hasFilters={total > 0}
            onClickReset={() => {
              setState({ brands: [], services: [], country: [], opened: [] })
              dispatch({ type: 'clear-filters' })
            }}
          >
            Filters
          </Header>
          <Content ref={ref}>
            <Accordion label={t('country', { ns: 'stores' })} filterCount={filters.country.length}>
              <Checkbox
                name="netherlands"
                label={t('netherlands', { ns: 'stores' })}
                checked={state.country.includes('netherlands')}
                onChange={() => toggle('country', 'netherlands')}
                mb="1rem"
              />
              <Checkbox
                name="singapore"
                label="Singapore"
                checked={state.country.includes('singapore')}
                onChange={() => toggle('country', 'singapore')}
                mb="1rem"
              />
            </Accordion>
            <Accordion
              label={t('openingHours', { ns: 'stores' })}
              filterCount={filters.opened.length}
            >
              <Checkbox
                name="opened-now"
                label={t('opendNow', { ns: 'stores' })}
                checked={state.opened.includes('opened-now')}
                onChange={() => toggle('opened', 'opened-now')}
                mb="1rem"
              />
              <Checkbox
                name="opened-evening"
                label={t('opendEvening', { ns: 'stores' })}
                checked={state.opened.includes('opened-evening')}
                onChange={() => toggle('opened', 'opened-evening')}
                mb="1rem"
              />
              <Checkbox
                name="opened-sunday"
                label={t('opendSunday', { ns: 'stores' })}
                checked={state.opened.includes('opened-sunday')}
                onChange={() => toggle('opened', 'opened-sunday')}
                mb="1rem"
              />
            </Accordion>
            <Accordion label="Services" filterCount={filters.services.length}>
              {services.map((service) => (
                <Checkbox
                  key={service.system.codename}
                  name={service.system.codename}
                  label={service.elements.title.value}
                  checked={state.services.includes(service.system.codename)}
                  onChange={() => toggle('services', service.system.codename)}
                  mb="1rem"
                />
              ))}
            </Accordion>
            <Accordion
              label={t('brandsInCollection', { ns: 'stores' })}
              filterCount={filters.brands.length}
            >
              {brands.map((brand) => (
                <Checkbox
                  key={brand.system.codename}
                  name={brand.system.codename}
                  label={brand.elements.title.value}
                  checked={state.brands.includes(brand.system.codename)}
                  onChange={() => toggle('brands', brand.system.codename)}
                  mb="1rem"
                />
              ))}
            </Accordion>
          </Content>
        </Background>
        {total > 0 && (
          <Footer onClickConfirm={handleSave}>{t('showStores', { ns: 'other' })}</Footer>
        )}
      </>
    )
  },
)

export default FilterOverlay
